/**
 * Shared dashboard parts (spec §2.1, D-TBD-6): `AgeStamp` (station data seen in flight: fresh, stale with a clock
 * glyph, or never seen, in game time) and `DockedAction` (a docked-only action shown disabled, never hidden, with
 * the reason, the nearest known station and Set course). The resolver `nearestStationWith` is card D4's.
 */
import type { ReactElement } from 'react'
import type { DashboardConfig } from './config'
import { formatDuration } from '../format'

const DAY_S = 86400

export type AgeLevel = 'here' | 'fresh' | 'aging' | 'stale' | 'never'

export interface Age {
  readonly level: AgeLevel
  readonly text: string
}

function fill(template: string, values: Readonly<Record<string, string>>): string {
  return template.replace(/\{(\w+)\}/g, (m, k: string) => values[k] ?? m)
}

/**
 * The "last seen" line for station data: `seenAt` and `now` in game seconds (null = never seen); `here` = docked
 * there (live). Fresh under `freshDays`, stale over `staleDays`.
 */
export function ageStamp(cfg: DashboardConfig['age'], station: string, seenAt: number | null, now: number, here: boolean): Age {
  if (here) return { level: 'here', text: cfg.here }
  if (seenAt === null) return { level: 'never', text: cfg.never }
  const ageS = Math.max(0, now - seenAt)
  const level: AgeLevel = ageS < cfg.freshDays * DAY_S ? 'fresh' : ageS > cfg.staleDays * DAY_S ? 'stale' : 'aging'
  return { level, text: fill(cfg.lastSeen, { station, age: formatDuration(ageS) }) }
}

export function AgeStamp({ age }: { age: Age }): ReactElement {
  return (
    <p className={`dash-age dash-age--${age.level}`} data-age={age.level}>
      {age.level === 'stale' ? (
        <span className="dash-age__clock" aria-hidden="true">
          ◷{' '}
        </span>
      ) : null}
      {age.text}
    </p>
  )
}

export interface NearestStation {
  readonly name: string
  /** "this system · 8.0 km", "2 jumps · 41 Gm". */
  readonly where: string
}

export interface DockedActionProps {
  readonly cfg: DashboardConfig['dockedAction']
  readonly label: string
  /** One line: "Dock at a yard to fit modules". */
  readonly reason: string
  readonly nearest: NearestStation | null
  onSetCourse(): void
}

export function DockedAction(p: DockedActionProps): ReactElement {
  return (
    <div className="dash-docked" data-docked-action="">
      <button type="button" className="dash-docked__action" disabled>
        {p.label}
      </button>
      <p className="dash-docked__reason">{p.reason}</p>
      {p.nearest !== null ? (
        <p className="dash-docked__nearest">
          {fill(p.cfg.nearest, { station: p.nearest.name, where: p.nearest.where })}{' '}
          <button type="button" className="dash-docked__course" onClick={p.onSetCourse}>
            <kbd>F</kbd> {p.cfg.setCourse}
          </button>
        </p>
      ) : (
        <p className="dash-docked__nearest">{p.cfg.none}</p>
      )}
    </div>
  )
}
